import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRestClient } from '../core/api/RestClientContext';
import { AdminUserInfoDetailsDto } from '../core/models/user';
import { Button } from '../core/components/Button';
import ExtendSubscriptionDialog from '../domains/users/components/ExtendSubscriptionDialog';
import './UserDetailsPage.css';

export const UserDetailsPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const restClient = useRestClient();
    const navigate = useNavigate();

    const [user, setUser] = useState<AdminUserInfoDetailsDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showExtendDialog, setShowExtendDialog] = useState(false);

    useEffect(() => {
        const fetchUserDetails = async () => {
            if (!userId) {
                setError('ID пользователя не указан');
                setLoading(false);
                return;
            }

            try {
                const userData = await restClient.getUserDetails(userId);
                setUser(userData);
            } catch (err) {
                setError('Ошибка загрузки данных пользователя');
            } finally {
                setLoading(false);
            }
        };

        fetchUserDetails();
    }, [userId, restClient]);

    if (loading) return <div className="user-details-page"><div className="loading">Загрузка...</div></div>;
    if (error) return <div className="user-details-page"><div className="error">{error}</div></div>;
    if (!user) return <div className="user-details-page"><div className="error">Пользователь не найден</div></div>;

    return (
        <div className="user-details-page compact">
            <div className="user-details-header">
                <Button variant="secondary" onClick={() => navigate(-1)}>← Назад</Button>
                <h2>Профиль</h2>
            </div>

            <div className="compact-grid">
                <div className="user-compact-section">
                    <div className="info">
                        <div><strong>{user.tgName}</strong></div>
                        <div>ID: {user.userId}</div>
                        <div>Telegram ID: {user.tgId}</div>
                    </div>
                </div>

                <div className="user-compact-section">
                    <div className="section-title">📦 Подписка</div>
                    {user.subInfo &&
                        <div className="info-grid">
                            <div><label>ID:</label><span>{user.subInfo.subId}</span></div>
                            <div><label>Тип:</label><span>{user.subInfo.type}</span></div>
                            <div><label>Начало:</label><span>{user.subInfo.beginDate}</span></div>
                            <div><label>Конец:</label><span>{user.subInfo.endDate}</span></div>
                        </div>
                    }
                    {!user.subInfo &&
                        <div>
                            Нет подписки
                        </div>
                    }
                </div>

                <div className="user-compact-section">
                    <div className="section-title">💳 Последние платежи</div>
                    {user.payments.length > 0 ? (
                        <table className="compact-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Тип</th>
                                    <th>Статус</th>
                                </tr>
                            </thead>
                            <tbody>
                                {user.payments.slice(0, 3).map(p => (
                                    <tr key={p.id}>
                                        <td>{p.youKassaPaymentId}</td>
                                        <td>{p.type}</td>
                                        <td>{p.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <div className="empty-message">Нет данных</div>}
                </div>
            </div>

            <div className="user-actions compact-actions">
                <Button onClick={() => setShowExtendDialog(true)}>Продлить подписку</Button>
                <Button>Уведомление</Button>
            </div>

            {showExtendDialog && (
                <ExtendSubscriptionDialog
                    userId={user.userId}
                    onClose={() => setShowExtendDialog(false)}
                    onSuccess={async () => {
                        // refresh user details
                        setLoading(true);
                        try {
                            const refreshed = await restClient.getUserDetails(user.userId);
                            setUser(refreshed);
                        } catch (err) {
                            // ignore, show toast maybe
                        } finally {
                            setLoading(false);
                        }
                    }}
                    onSubmit={async (data) => {
                        // data: { durationDays?: number; end?: string; request?: NotificationRequest }
                        const params: any = {};
                        if (data.durationDays) params.durationDays = data.durationDays;
                        if (data.end) params.end = data.end; // backend expects endTime param

                        await restClient.updateUserSub(user.userId, params, data.request);
                    }}
                />
            )}
        </div>
    );
};